import { createSlice } from '@reduxjs/toolkit';
import type { AIChatMessage } from './types';

// State interface
interface AIChatState {
  messages: AIChatMessage[];
  isLoading: boolean;
  error: boolean;
  topicGuid: string | null;
  topicTitle: string | null;
}

// Initial state - content is i18n key, resolved in UI
const initialState: AIChatState = {
  messages: [],
  isLoading: false,
  error: false,
  topicGuid: null,
  topicTitle: null
};

// AI Chat slice
export const slice = createSlice({
  name: 'aiChat',
  initialState,
  reducers: {
    // Add user message
    addUserMessage: (state, action) => {
      const userMessage = {
        id: Date.now().toString(),
        content: action.payload,
        sender: 'user' as const,
        timestamp: new Date()
      };
      state.messages.push(userMessage);
    },

    // Add AI message
    addAIMessage: (state, action) => {
      state.messages.push(action.payload);
    },

    // Load conversation history (replace entirely so empty history can trigger opening message)
    loadConversationHistory: (state, action) => {
      state.messages = action.payload;
    },

    // Set topic context
    setTopicContext: (state, action) => {
      state.topicGuid = action.payload.topicGuid;
      state.topicTitle = action.payload.topicTitle;
    },

    // Start loading
    startLoading: (state) => {
      state.isLoading = true;
      state.error = false;
    },

    // Stop loading
    stopLoading: (state) => {
      state.isLoading = false;
    },

    // Handle error
    hasError: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Clear messages (empty list; UI shows empty state or opening message is fetched by caller)
    clearMessages: (state) => {
      state.messages = [];
    },

    // Clear error
    clearError: (state) => {
      state.error = false;
    },

    // Reset state
    resetAIChat: (state) => {
      state.messages = initialState.messages;
      state.isLoading = false;
      state.error = false;
      state.topicGuid = null;
      state.topicTitle = null;
    }
  }
});

export default slice.reducer;
